/**
 * Keeping the launcher off the page's own sticky bottom bar.
 *
 * Found on Barkyn (Sophie, 22 Sep 2026): their plan page pins a purchase bar
 * to the bottom of the viewport (`.lpr-pricing` — 152px on a phone, 121px on
 * desktop), and the launcher plus its "Fale com um assistente" caption sat
 * inside it, covering the price. No fixed offset can fix that for good: the
 * bar exists on ONE page, and its height moves with the viewport and with
 * their own copy.
 *
 * So a widget can opt in (`launcher.avoid_bottom_bars`) and the launcher
 * MEASURES whatever is pinned to the bottom of its own column, then sits
 * above it. With nothing there — every other page, every other client — the
 * launcher renders exactly where it always did, so the flag is the only
 * thing that changes behaviour.
 *
 * The pure half (what counts as a bar, what the offset becomes) is tested by
 * scripts/test-bottom-bar.mjs; the DOM half is three elementsFromPoint hits.
 */

/** Breathing room between the bar and the launcher when one is found. */
export const BOTTOM_BAR_GAP = 12;

/** The ids we draw with: the loader's own launcher + caption, and the bundle's
 *  shadow host. A point sample lands on these, and ours must never be mistaken
 *  for the page's bar. */
export const WIDGET_ELEMENT_IDS = [
  "flowiq-loader-launcher",
  "flowiq-loader-launcher-caption",
  "react-chat-widget-container",
];

export interface BarBox {
  /** Rendered height in px. */
  height: number;
  /** Rendered width in px. */
  width: number;
  /** Distance from the viewport top to the element's bottom edge. */
  bottom: number;
}

/**
 * Is this element the page's bottom bar?
 *
 * Deliberately narrow, because a false positive MOVES the launcher:
 *  - fixed or sticky (a bar that scrolls away is not in our way),
 *  - pinned to the bottom edge (within 4px),
 *  - at least half the viewport wide (a bar, not a chip or a back-to-top dot),
 *  - between 16px and 40% of the viewport tall (a bar, not a full-screen
 *    drawer — Barkyn's own side-cart is `fixed` and the full height).
 */
export function barQualifies(
  box: BarBox,
  position: string,
  viewportWidth: number,
  viewportHeight: number,
): boolean {
  if (position !== "fixed" && position !== "sticky") return false;
  if (!(viewportWidth > 0) || !(viewportHeight > 0)) return false;
  if (box.height < 16 || box.height > viewportHeight * 0.4) return false;
  if (box.width < viewportWidth * 0.5) return false;
  return box.bottom >= viewportHeight - 4;
}

/** The gap the launcher actually renders with: its configured offset, or clear
 *  of the bar, whichever is further from the edge. No bar = untouched. */
export function liftedOffset(offset: number, barHeight: number): number {
  if (!(barHeight > 0)) return offset;
  return Math.max(offset, Math.round(barHeight) + BOTTOM_BAR_GAP);
}

/** The horizontal centre of the launcher — the column we sample. */
export function launcherAnchorX(
  viewportWidth: number,
  corner: "left" | "right",
  offsetX: number,
  size: number,
): number {
  const centre =
    corner === "left" ? offsetX + size / 2 : viewportWidth - offsetX - size / 2;
  return Math.min(Math.max(Math.round(centre), 1), Math.max(viewportWidth - 1, 1));
}

interface MinimalElement {
  id?: string;
  parentElement?: MinimalElement | null;
  getBoundingClientRect(): { height: number; width: number; bottom: number };
}

function isOurs(el: MinimalElement | null | undefined): boolean {
  let node: MinimalElement | null | undefined = el;
  while (node) {
    if (node.id && WIDGET_ELEMENT_IDS.includes(node.id)) return true;
    node = node.parentElement;
  }
  return false;
}

/**
 * Height of the bar pinned under the launcher's column, or 0.
 *
 * Samples three rows just above the bottom edge (a bar with a shadow or a
 * rounded lip can leave the very last row to the page), walks each hit up to
 * its nearest qualifying fixed/sticky ancestor, and takes the tallest.
 */
export function measureBottomBar(
  doc: Document,
  win: Window,
  anchorX: number,
): number {
  if (typeof doc?.elementsFromPoint !== "function") return 0;
  const viewportWidth = win.innerWidth;
  const viewportHeight = win.innerHeight;
  if (!(viewportWidth > 0) || !(viewportHeight > 0)) return 0;
  const x = Math.min(Math.max(Math.round(anchorX), 1), viewportWidth - 1);

  let tallest = 0;
  for (const y of [viewportHeight - 2, viewportHeight - 12, viewportHeight - 28]) {
    if (y < 1) continue;
    let hits: Element[] = [];
    try {
      hits = doc.elementsFromPoint(x, y) || [];
    } catch {
      continue;
    }
    for (const hit of hits) {
      let node: Element | null = hit;
      while (node && node !== doc.body && node !== doc.documentElement) {
        if (isOurs(node as unknown as MinimalElement)) break;
        let style: CSSStyleDeclaration | null = null;
        try {
          style = win.getComputedStyle(node);
        } catch {
          style = null;
        }
        if (style) {
          const r = node.getBoundingClientRect();
          const visible =
            style.visibility !== "hidden" &&
            style.display !== "none" &&
            Number(style.opacity || "1") > 0.05;
          if (
            visible &&
            barQualifies(
              { height: r.height, width: r.width, bottom: r.bottom },
              style.position,
              viewportWidth,
              viewportHeight,
            )
          ) {
            tallest = Math.max(tallest, Math.round(r.height));
            break;
          }
        }
        node = node.parentElement;
      }
    }
  }
  return tallest;
}

/**
 * Keep a measurement current while the page lives.
 *
 * `apply` is called only when the height CHANGES. Bars mount late (Barkyn's
 * renders after their own JS), move with the viewport, and can slide in on
 * scroll, so: a few staggered re-measures after mount, then scroll + resize.
 * Throttled with a timer rather than rAF — a background tab never paints, and
 * the widget must still be correct when it comes forward.
 */
export function watchBottomBar(
  doc: Document,
  win: Window,
  anchorX: () => number,
  apply: (barHeight: number) => void,
): () => void {
  let last = -1;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;

  const measure = () => {
    timer = null;
    if (stopped) return;
    const h = measureBottomBar(doc, win, anchorX());
    if (h !== last) {
      last = h;
      apply(h);
    }
  };
  const schedule = () => {
    if (stopped || timer) return;
    timer = setTimeout(measure, 100);
  };

  measure();
  const delayed = [300, 900, 2000, 4000].map((ms) => setTimeout(measure, ms));
  win.addEventListener("scroll", schedule, { passive: true });
  win.addEventListener("resize", schedule);

  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
    delayed.forEach(clearTimeout);
    win.removeEventListener("scroll", schedule);
    win.removeEventListener("resize", schedule);
  };
}
